import React, { ReactElement } from 'react';

import { observer } from 'mobx-react';

import classes from './CustomerRow.module.scss';
import {
  CheckboxCell,
  GridTableCell,
  GridTableRow,
  UserInfoCell,
} from '../../../../shared/components/table-components';
import { CustomerContactLink } from '../../../../shared/components/buttons';
import { StatusCell } from './StatusCell/StatusCell';
import { CustomerCommunicationInfo } from '../../../interfaces/CustomerCommunicationInfo';
import { getFormattedDate } from '../../../../shared/utils/dateUtils';
import {
  localDataFormat,
  oldDateFormat,
} from '../../../../shared/constants/apiDateFormat';
import { customerCommunicationStore } from '../../../stores/customerCommunicationStore';
import { getFlagsObject } from '../../../../shared/utils/sortingAndGroupingUtil';
import { USER_ACTIVATION_STATUSES } from '../../../constants/USER_ACTIVATION_STATUSES';
import goHighLevelLogo from '../../../../shared/assets/image/goHighLevelLogo.webp';
import { H20BStatus } from './H20BStatus/H20BStatus';
import {
  CUSTOMER_COMMUNICATION_COLUMN,
  CustomerCommunicationColumn,
} from '../NgzCustomerCommunicationList.settings';

type CustomerRowProps = {
  userInfo: CustomerCommunicationInfo;
  visibleColumns: CustomerCommunicationColumn[];
};

const CustomerRow = observer(({ userInfo, visibleColumns }: CustomerRowProps) => {
  const { selectMethod } = customerCommunicationStore;

  const { getIsSelected, selectItem } = selectMethod;

  const coachName = `${userInfo.coachFirstName} ${userInfo.coachLastName}`;

  const isChecked = getIsSelected(userInfo.id);
  const handleCheck = () => {
    selectItem(userInfo.id);
  };

  const activationStatus = getFlagsObject(USER_ACTIVATION_STATUSES, [userInfo.pStatus]);

  const userLink = activationStatus.PREREGISTERED
    ? `#!/customer-setup/flow-2024/${userInfo.id}`
    : `#!/customer-profile/${userInfo.id}`;

  const isGHLConnected = !!userInfo.ghlContactId;

  const renderColumnsInfo: Record<CustomerCommunicationColumn, ReactElement> = {
    [CUSTOMER_COMMUNICATION_COLUMN.NAME]: (
      <CheckboxCell showCheckbox={true} onCheck={handleCheck} isChecked={isChecked}>
        <UserInfoCell
          firstName={userInfo.firstName}
          lastName={userInfo.lastName}
          email={userInfo.email}
          phone={userInfo.phone}
          href={userLink}
          dataTestId="customer-info-cell"
          nameAdornment={
            <div className={classes.statusContainer}>
              {isGHLConnected && (
                <img src={goHighLevelLogo} alt="GoHighLevel logo" width={16} />
              )}
              <H20BStatus status={userInfo.h20bScaleStatus} />
            </div>
          }
        />
      </CheckboxCell>
    ),
    [CUSTOMER_COMMUNICATION_COLUMN.LOCATION]: (
      <GridTableCell dataTestId="location-cell">{userInfo.gymName}</GridTableCell>
    ),
    [CUSTOMER_COMMUNICATION_COLUMN.GROUP]: (
      <GridTableCell dataTestId="group-cell">{userInfo.customerGroup}</GridTableCell>
    ),
    [CUSTOMER_COMMUNICATION_COLUMN.COACH]: (
      <GridTableCell dataTestId="coach-cell" className="private-info">
        {coachName}
      </GridTableCell>
    ),
    [CUSTOMER_COMMUNICATION_COLUMN.START_DATE]: (
      <GridTableCell dataTestId="start-cell">
        {getFormattedDate(userInfo.registrationDate, localDataFormat, oldDateFormat)}
      </GridTableCell>
    ),
    [CUSTOMER_COMMUNICATION_COLUMN.STATUS]: (
      <StatusCell
        activationStatus={userInfo.pStatus}
        subscriptionExpire={userInfo.subscriptionExpire}
      />
    ),
    [CUSTOMER_COMMUNICATION_COLUMN.CUSTOM_FILTER]: <div />,
    [CUSTOMER_COMMUNICATION_COLUMN.ACTIONS]: (
      <GridTableCell justify="end" dataTestId="contact-cell">
        <CustomerContactLink customerId={userInfo.id} resourceType="customers" />
      </GridTableCell>
    ),
  };

  return (
    <GridTableRow
      variant="row"
      highlight={userInfo.newScansAvailable}
      dataTestId="customer-row"
    >
      {visibleColumns.map((col) => (
        <React.Fragment key={col}>{renderColumnsInfo[col]}</React.Fragment>
      ))}
    </GridTableRow>
  );
});

export { CustomerRow };
